/**
 * Browser-session memory of data a principal was just authorized to read,
 * bound to that principal. An entry is served only to the same confirmed
 * identity, never without one, and every memory is wiped synchronously when
 * the confirmed identity changes (sign-out, account switch). Callers use it
 * only as a placeholder while the live, access-checked query loads, and
 * forget an entry as soon as that query refuses it.
 */
export type IdentityScopedSessionMemory<V> = {
  get: (identity: string | null, key: string) => V | null;
  set: (identity: string | null, key: string, value: V) => void;
  forget: (key: string) => void;
  clear: () => void;
  /**
   * Bumped by every `clear`. Async work that read or will write this memory
   * captures it first and drops its result if it moved, so a completion that
   * started before a sign-out or account switch cannot repopulate it.
   */
  generation: () => number;
};

const memories = new Set<{ clear: () => void; keepOnSignIn?: boolean }>();

export function createIdentityScopedSessionMemory<V>(
  limit: number
): IdentityScopedSessionMemory<V> {
  const entries = new Map<string, { identity: string; value: V }>();
  let generation = 0;
  const memory: IdentityScopedSessionMemory<V> = {
    get(identity, key) {
      if (typeof window === 'undefined' || !identity) return null;
      const entry = entries.get(key);
      return entry && entry.identity === identity ? entry.value : null;
    },
    set(identity, key, value) {
      if (typeof window === 'undefined' || !identity) return;
      entries.delete(key);
      entries.set(key, { identity, value });
      if (entries.size > limit) {
        const oldest = entries.keys().next().value;
        if (oldest !== undefined) entries.delete(oldest);
      }
    },
    forget(key) {
      entries.delete(key);
    },
    clear() {
      entries.clear();
      generation += 1;
    },
    generation() {
      return generation;
    },
  };
  memories.add(memory);
  return memory;
}

/**
 * Registers a module-level store that holds data or URLs authorized for the
 * current principal, so it is wiped with the memories above. For stores that
 * can't use `createIdentityScopedSessionMemory` (web storage, custom expiry).
 *
 * `keepOnSignIn` keeps the store when the previous identity was signed out:
 * web-storage handoffs written before sign-in (onboarding, pending sends)
 * belong to the person signing in. Leaving or switching an identity always
 * wipes.
 */
export function registerIdentityScopedSessionClear(
  clear: () => void,
  options?: { keepOnSignIn?: boolean }
): void {
  memories.add({ clear, keepOnSignIn: options?.keepOnSignIn === true });
}

let identityEpoch = 0;

/**
 * Moves on every identity-change wipe. Async work that will write into an
 * identity-scoped store (an image load, a fetch, a parse) captures it before
 * it starts and drops the write if it moved: a clear only empties what is
 * already stored, so without this a late continuation started for the
 * previous identity would repopulate the store for the next one (#4628).
 */
export function identitySessionEpoch(): number {
  return identityEpoch;
}

/**
 * Captures the current epoch; the returned check is true while it still is.
 * Call before async work, test before writing its result into a store.
 */
export function identitySessionFence(): () => boolean {
  const epoch = identityEpoch;
  return () => epoch === identityEpoch;
}

/** True while `epoch` (from `identitySessionEpoch()`) is still current. */
export function isIdentitySessionEpochCurrent(epoch: number): boolean {
  return epoch === identityEpoch;
}

/**
 * Called by the Convex auth lifecycle on every confirmed-identity change, with
 * the identity being left (null = signed out). Without it, everything clears.
 */
export function clearIdentityScopedSessionMemories(
  previousIdentity?: string | null
): void {
  identityEpoch += 1;
  for (const memory of memories) {
    if (memory.keepOnSignIn && previousIdentity === null) continue;
    memory.clear();
  }
}
